/*
 * Copyright (c) 2026 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { FilterMetadata } from 'primeng/api';

export type TableFilters = Record<string, FilterMetadata | FilterMetadata[]>;

export function normalizeFilters(filters: TableFilters): TableFilters {
  return Object.entries(filters).reduce((acc, [key, filter]) => {
    if (Array.isArray(filter)) {
      acc[key] = filter.map((item) => normalizeFilterMetadata(item));
    } else {
      acc[key] = normalizeFilterMetadata(filter);
    }
    return acc;
  }, {} as TableFilters);
}

export function normalizeFilterMetadata(filter: FilterMetadata): FilterMetadata {
  if (!filter || typeof filter !== 'object') return filter;
  const normalizedValue = normalizeFilterValue(filter.value);
  if (normalizedValue === filter.value) return filter;
  return { ...filter, value: normalizedValue };
}

export function normalizeFilterValue(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map((item) => normalizeFilterValue(item));
  if (value instanceof Date) return formatLocalDateTime(value);
  if (isRangeObject(value)) return normalizeDateRange(value);
  if (typeof value === 'string') return normalizeDateString(value);
  return value;
}

export function isRangeObject(value: unknown): value is { startDate?: unknown; endDate?: unknown } {
  return (
    !!value &&
    typeof value === 'object' &&
    ('startDate' in (value as Record<string, unknown>) || 'endDate' in (value as Record<string, unknown>))
  );
}

export function normalizeDateRange(value: { startDate?: unknown; endDate?: unknown }) {
  const startHasZ = typeof value.startDate === 'string' && value.startDate.endsWith('Z');
  const endHasZ = typeof value.endDate === 'string' && value.endDate.endsWith('Z');
  const start = toLocalDateObject(value.startDate);
  const end = toLocalDateObject(value.endDate);
  return {
    startDate: start ? formatLocalDateTime(withTime(start, 0, 0, 0)) + (startHasZ ? 'Z' : '') : null,
    endDate: end ? formatLocalDateTime(withTime(end, 23, 59, 59)) + (endHasZ ? 'Z' : '') : null,
  };
}

export function normalizeDateString(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})?$/.test(value)) {
    return value;
  }
  const localDate = toLocalDateObject(value);
  return localDate ? formatLocalDateTime(localDate) : value;
}

export function toLocalDateObject(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return new Date(value.getTime());
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();

  if (/\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split('-').map(Number);
    if ([year, month, day].some(Number.isNaN)) return null;
    return new Date(year, month - 1, day, 0, 0, 0);
  }

  if (/[Z]|[+-]\d{2}:\d{2}$/.test(trimmed)) {
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match.map(Number);
  if ([y, mo, d].some(Number.isNaN)) return null;
  return new Date(y, mo - 1, d, h || 0, mi || 0, s || 0);
}

export function withTime(date: Date, hours: number, minutes: number, seconds: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, seconds);
}

export function formatLocalDateTime(date: Date): string {
  const pad = (v: number) => v.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}
